import React from 'react';
import { ICampaignMetrics, ICampaign } from '../../interface/ICampaign';
import { IGuestSentiment } from '../../interface/IGuestSentiment';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	Typography,
	CircularProgress,
	Box,
} from '@mui/material';
import style from './CampaignMetrics.module.scss';

interface CampaignMetricsProps {
	metrics: ICampaignMetrics;
	guestSentiment?: IGuestSentiment | null;
	onSentimentClick?: () => void;
	campaigns?: ICampaign[];
	campaignStats?: Map<string, { submissions: number; averageRating: number | null }>;
	isLoading?: boolean;
}

export default function CampaignMetrics({
	metrics,
	guestSentiment,
	onSentimentClick,
	campaigns = [],
	campaignStats,
	isLoading = false,
}: CampaignMetricsProps): JSX.Element {
	// Sort campaigns by most recent (createdAt) and take top 10
	const recentCampaigns = [...campaigns]
		.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
		.slice(0, 10);
	return (
		<div className={style.MetricsContainer}>
			<div
				className={style.MetricBox}
				onClick={onSentimentClick}
				style={{ cursor: onSentimentClick ? 'pointer' : 'default' }}
			>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					Mabel A.I. Guest Sentiment
				</Typography>
				<h3></h3>
				<div className={style.MetricValue}>
					<span className={style.Score}>
						{guestSentiment?.sentimentScore || metrics.averageSentimentScore.toFixed(1)}
					</span>
					<span className={style.Label}>Sentiment Score</span>
				</div>
				{guestSentiment && (
					<div className={style.MetricDetails}>
						<div className={style.DetailItem}>
							<span className={style.DetailLabel}>Top Likes</span>
							<span className={style.DetailValue}>
								{guestSentiment.topLikes.slice(0, 2).join(', ')}
							</span>
						</div>
						<div className={style.DetailItem}>
							<span className={style.DetailLabel}>Top Dislikes</span>
							<span className={style.DetailValue}>
								{guestSentiment.topDislikes.slice(0, 2).join(', ')}
							</span>
						</div>
					</div>
				)}
				{!guestSentiment && (
					<div className={style.MetricDetails}>
						<div className={style.DetailItem}>
							<span className={style.DetailLabel}>Total Responses</span>
							<span className={style.DetailValue}>{metrics.totalResponses}</span>
						</div>
						<div className={style.DetailItem}>
							<span className={style.DetailLabel}>Response Rate</span>
							<span className={style.DetailValue}>{metrics.averageResponseRate.toFixed(1)}%</span>
						</div>
					</div>
				)}
			</div>

			<div className={style.MetricBox}>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					Recent Campaigns
				</Typography>
				<div className={style.CampaignListContainer}>
					{isLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
							<CircularProgress size={40} color="inherit" />
						</Box>
					) : recentCampaigns.length > 0 ? (
						<TableContainer component={Paper} sx={{ boxShadow: 'none', maxHeight: 400 }}>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow sx={{ backgroundColor: '#f5f5f5' }}>
										<TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
											Campaign
										</TableCell>
										<TableCell
											align="center"
											sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: 100 }}
										>
											Score
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{recentCampaigns.map((campaign) => {
										const stats = campaignStats?.get(campaign.id);
										const rating = stats?.averageRating;
										
										// Check if campaign is active (compare dates only, not time)
										const now = new Date();
										const todayStr = now.toISOString().split('T')[0];
										const startDateStr = campaign.startDate.split('T')[0];
										const endDateStr = campaign.endDate.split('T')[0];
										const isActive = todayStr >= startDateStr && todayStr <= endDateStr;

										return (
											<TableRow key={campaign.id} hover>
												<TableCell>
													<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
														<span>{campaign.title}</span>
														{isActive && (
															<Chip
																label="Active"
																size="small"
																sx={{
																	backgroundColor: '#4caf50',
																	color: 'white',
																	fontWeight: 600,
																	fontSize: '0.7rem',
																	height: '20px',
																}}
															/>
														)}
													</div>
												</TableCell>
												<TableCell align="center">
													{rating !== null && rating !== undefined ? (
														<span style={{ color: '#446388', fontWeight: 'bold' }}>
															{rating.toFixed(2)}
														</span>
													) : (
														'-'
													)}
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					) : (
						<div className={style.EmptyState}>No campaigns yet</div>
					)}
				</div>
			</div>
		</div>
	);
}
